import { Divider, Typography } from '@scaleaq/scaleui';
import { StreamSourceTransport, type WebRtcStreamStatus } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { JITTER_FORMAT, PERCENT_VALUE_FORMAT } from '@/constants';
import { useNumberFormat } from '@/localization';
import { Icon } from '@/utils';
import {
  getSourceStatPresence,
  isSourceStatusStale,
  type ClientStreamStats,
  type StreamHealthCause,
} from '@/utils/streamHealth.functions.ts';

import {
  StyledCameraStreamHealthTooltip,
  StyledStaleNote,
} from './cameraStreamHealthTooltip/cameraStreamHealthTooltip.styles.ts';

type CameraStreamHealthTooltipProps = {
  state: RTCPeerConnectionState;
  isVideoPaused: boolean;
  isRewinding: boolean;
  cause?: StreamHealthCause;
  clientStats?: ClientStreamStats & { transport?: 'udp' | 'tcp' };
  streamStatus?: WebRtcStreamStatus;
};

/** Both legs of the video path: camera → mediaserver (source), mediaserver → browser (client). */
export const CameraStreamHealthTooltip = ({
  state,
  isVideoPaused,
  isRewinding,
  cause,
  clientStats,
  streamStatus,
}: CameraStreamHealthTooltipProps) => {
  const { t } = useTranslation(['cameraControl', 'networkHealth', 'common']);
  const { formatNumber, formatInteger } = useNumberFormat();

  const sourceStats = streamStatus?.sourceStats;
  const sourceJitter = getSourceStatPresence({
    sourceStats,
    value: sourceStats?.inboundRtpPacketsJitterMs,
  });
  // The 60 s window: what moves the light, unlike the lifetime figure.
  const sourcePacketLoss = getSourceStatPresence({
    sourceStats,
    value: sourceStats?.windowPacketLossPercent,
  });

  const getConnectionText = () => {
    if (isRewinding) return t('cameraControl:dvr.rewinding');
    if (isVideoPaused) return t('cameraControl:videoPaused');
    if (state === 'connected') return t('common:connected');
    if (state === 'connecting') return t('common:connecting');
    return t('common:disconnected');
  };

  // HLS rewind has no client reading yet (SCO-2749).
  const showClient = !isRewinding && !isVideoPaused && state === 'connected' && clientStats;

  return (
    <StyledCameraStreamHealthTooltip>
      <Typography type='body_small'>
        {t('cameraControl:videoStream')}: {getConnectionText()}
      </Typography>
      {cause && (
        <Typography type='body_small'>{t(`cameraControl:healthCause.${cause}`)}</Typography>
      )}
      <Divider color='soft_strong' />
      {isRewinding && (
        <Typography type='body_small' secondary>
          {t('cameraControl:sourceHealthNow')}
        </Typography>
      )}
      {sourceStats ? (
        <>
          <Typography type='body_small'>
            {sourceJitter.kind === 'value'
              ? t('cameraControl:sourceJitterWithUnit', {
                  value: formatNumber(sourceJitter.value, JITTER_FORMAT),
                })
              : t('cameraControl:sourceJitterUnavailable', { reason: t(sourceJitter.reasonKey) })}
          </Typography>
          {/* UDP is the expected transport, so only an exception gets a line. */}
          {sourceStats.transport !== StreamSourceTransport.Udp && (
            <Typography type='body_small'>
              {t('cameraControl:sourceTransport', {
                transport: t(`networkHealth:transportType.${sourceStats.transport}`),
              })}
            </Typography>
          )}
          <Typography type='body_small'>
            {sourcePacketLoss.kind === 'value'
              ? t('cameraControl:sourcePacketLossWithUnit', {
                  value: formatNumber(sourcePacketLoss.value, PERCENT_VALUE_FORMAT),
                })
              : t('cameraControl:sourcePacketLossUnavailable', {
                  reason: t(sourcePacketLoss.reasonKey),
                })}
          </Typography>
          {sourceStats.inboundRtpPacketsInError != null && (
            <Typography type='body_small'>
              {t('cameraControl:sourcePacketErrors', {
                value: formatInteger(sourceStats.inboundRtpPacketsInError),
              })}
            </Typography>
          )}
          {/* Why the source is down: the case behind a Critical light with no loss to show. */}
          {sourceStats.lastError && (
            <Typography type='body_small'>
              {t('networkHealth:lastError', { error: sourceStats.lastError })}
            </Typography>
          )}
          {/* Pushes come only on change, so a quiet stream ages too; flag it, do not alarm. */}
          {isSourceStatusStale({ lastUpdated: streamStatus?.lastUpdated, nowMs: Date.now() }) && (
            <StyledStaleNote>
              <Icon.TriangleAlert aria-hidden />
              <Typography type='body_small'>{t('cameraControl:sourceStale')}</Typography>
            </StyledStaleNote>
          )}
        </>
      ) : (
        <Typography type='body_small'>{t('cameraControl:sourceUnavailable')}</Typography>
      )}
      {showClient && (
        <>
          <Divider color='soft_strong' />
          <Typography type='body_small'>
            {t('cameraControl:clientJitterWithUnit', {
              value: formatNumber(clientStats.clientJitterMs, JITTER_FORMAT),
            })}
          </Typography>
          {clientStats.transport && clientStats.transport !== 'udp' && (
            <Typography type='body_small'>
              {t('cameraControl:clientTransport', {
                transport: t(
                  `networkHealth:transportType.${CLIENT_TRANSPORT_TYPE[clientStats.transport]}`
                ),
              })}
            </Typography>
          )}
          <Typography type='body_small'>
            {t('cameraControl:clientPacketLossWithUnit', {
              value: formatNumber(clientStats.clientPacketLossPercent, PERCENT_VALUE_FORMAT),
            })}
          </Typography>
          {/* Retransmission and keyframe requests: what the browser did about loss. Windowed
              figures, so only shown while non-zero; `!!` keeps a 0 from rendering as text. */}
          {!!clientStats.clientNackPercent && (
            <Typography type='body_small'>
              {t('cameraControl:clientNACKPercent', {
                value: formatNumber(clientStats.clientNackPercent, PERCENT_VALUE_FORMAT),
              })}
            </Typography>
          )}
          {!!clientStats.clientPliCount && (
            <Typography type='body_small'>
              {t('cameraControl:clientPLICount', {
                value: formatInteger(clientStats.clientPliCount),
              })}
            </Typography>
          )}
        </>
      )}
    </StyledCameraStreamHealthTooltip>
  );
};

/** ICE candidate protocol, named as the source transport is so both legs read alike. */
const CLIENT_TRANSPORT_TYPE = {
  tcp: StreamSourceTransport.Tcp,
  udp: StreamSourceTransport.Udp,
} as const;
